import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import axios from "axios";
import consumer from "../consumer";
import type {
  ConversationResponse,
  Broadcast,
  BaseMessage,
  ConversationUpdateBroadcast,
  OnboardingData,
} from "../types";

interface AskQuestionParams {
  conversationId: number;
  question: string;
}

export function useConversation(conversationId: number) {
  return useQuery<ConversationResponse>({
    queryKey: ["conversation", conversationId],
    queryFn: async () => {
      const { data } = await axios.get(`/app/ai_chats/${conversationId}.json`);
      return data;
    },
    enabled: !!conversationId,
  });
}

export function useAskQuestion() {
  return useMutation({
    mutationFn: async ({ conversationId, question }: AskQuestionParams) => {
      const { data } = await axios.post(`/app/ai_chats/${conversationId}/ask`, {
        question,
      });
      return data;
    },
  });
}

export function useOnboarding(conversationId: number) {
  return useQuery<OnboardingData>({
    queryKey: ["onboarding", conversationId],
    queryFn: async () => {
      const { data } = await axios.get(`/app/ai_chats/onboarding`, {
        params: { conversation_id: conversationId },
      });
      return data;
    },
    enabled: !!conversationId,
  });
}

export function useDismissOnboarding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (conversationId: number) => {
      const { data } = await axios.post(`/app/ai_chats/dismiss_onboarding`, {
        conversation_id: conversationId,
      });
      return data;
    },
    onSuccess: (_, conversationId) => {
      // Update onboarding query cache to hide the message
      queryClient.setQueryData<OnboardingData>(
        ["onboarding", conversationId],
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            onboarding: { show: false, message: null },
          };
        },
      );
    },
  });
}

export function useConversationSocket(conversationId: number) {
  const queryClient = useQueryClient();

  useEffect(() => {
    const channel = consumer.subscriptions.create(
      { channel: "AiChatChannel", conversation_id: conversationId },
      {
        connected() {
          console.log(
            "✅ ActionCable connected for conversation:",
            conversationId,
          );
        },
        disconnected() {
          console.log("❌ ActionCable disconnected");
        },
        received(broadcast: Broadcast) {
          console.log("📡 Broadcast received:", broadcast);

          // Check if this is a conversation update or a message
          if ("type" in broadcast && broadcast.type === "conversation_update") {
            // Handle conversation update (e.g., title change)
            const conversationUpdate = broadcast as ConversationUpdateBroadcast;
            queryClient.setQueryData<ConversationResponse>(
              ["conversation", conversationId],
              (oldData) => {
                if (!oldData) return oldData;
                return { ...oldData, title: conversationUpdate.title };
              },
            );
          } else {
            // Handle message broadcast
            const message = broadcast as BaseMessage;
            queryClient.setQueryData<ConversationResponse>(
              ["conversation", conversationId],
              (oldData) => {
                if (!oldData) return oldData;

                const existingIndex = oldData.conversation_messages.findIndex(
                  (m) => m.id === message.id,
                );

                if (existingIndex >= 0) {
                  // Update existing message
                  const updated = [...oldData.conversation_messages];
                  updated[existingIndex] = message;
                  return { ...oldData, conversation_messages: updated };
                } else {
                  // Append new message
                  return {
                    ...oldData,
                    conversation_messages: [
                      ...oldData.conversation_messages,
                      message,
                    ],
                  };
                }
              },
            );
          }
        },
      },
    );

    return () => channel.unsubscribe();
  }, [conversationId, queryClient]);
}
